/**
 * Pure reistijd-afwijking-helpers (batch-8 rapportage).
 *
 * Géén `node:*`-imports en geen DB — puur reken-/statistiekwerk zodat het
 * unit-getest wordt en zowel in de API-route (rapportage Deel 2) als in de
 * detectie-cron (Deel 3) hergebruikt kan worden.
 *
 * `mediaan` wordt in Deel 2 gebruikt voor de mediaan-afwijking per
 * medewerker→klant; in Deel 3 voegt `detecteerReistijdAfwijking` de
 * week-signaal-detectie toe (instelbare drempels, geen hardcoded waarden).
 */

/** Mediaan van een lijst getallen. Lege lijst → 0. Muteert de input niet. */
export function mediaan(getallen: number[]): number {
  if (getallen.length === 0) return 0
  const gesorteerd = [...getallen].sort((a, b) => a - b)
  const midden = Math.floor(gesorteerd.length / 2)
  if (gesorteerd.length % 2 === 0) {
    return (gesorteerd[midden - 1]! + gesorteerd[midden]!) / 2
  }
  return gesorteerd[midden]!
}

/** Gemiddelde van een lijst getallen. Lege lijst → 0. */
export function gemiddelde(getallen: number[]): number {
  if (getallen.length === 0) return 0
  return getallen.reduce((s, v) => s + v, 0) / getallen.length
}

// ─── Indien-validatie: "is er überhaupt een afwijking?" ──────────────────────

/**
 * Een reisafwijking is pas zinvol als de werkelijke reistijd of -afstand
 * daadwerkelijk van de berekende standaard verschilt. Een monteur kan anders een
 * "afwijking" indienen die niets verandert (werkelijk == berekend) — die
 * verschijnt dan als "afwijking 0 min" in de goedkeur-wachtrij. Deze pure helper
 * is de gedeelde regel voor zowel de portal (client-side, vóór verzturen) als de
 * API (server-side, autoritair).
 *
 * Drempels: ≥ 1 minuut tijdverschil OF ≥ 1 km afstandsverschil telt als
 * afwijking. De portal-dropdown werkt in stappen van 15 min, dus 1 min scheidt
 * "geen afwijking" (0) cleanly van een echte keuze; de km-input is heeltallig,
 * dus een afrondingsverschil (bv. 86,94 → 87) valt onder de drempel.
 *
 * NIET_GEREDEN (werkelijk 0 t.o.v. berekend > 0) levert vanzelf een verschil op
 * en hoeft hier niet apart te worden uitgezonderd.
 */
export const REIS_AFWIJKING_MIN_DELTA_MINUTEN = 1
export const REIS_AFWIJKING_MIN_DELTA_KM = 1

export function heeftReisAfwijking(input: {
  werkelijkeReistijdUren: number | null | undefined
  werkelijkeReisKm: number | null | undefined
  berekendeReistijdUren: number
  berekendeReisKm: number
}): boolean {
  const tijdVerschilMin =
    input.werkelijkeReistijdUren == null
      ? 0
      : Math.abs(input.werkelijkeReistijdUren - input.berekendeReistijdUren) * 60
  const kmVerschil =
    input.werkelijkeReisKm == null
      ? 0
      : Math.abs(input.werkelijkeReisKm - input.berekendeReisKm)
  return (
    tijdVerschilMin >= REIS_AFWIJKING_MIN_DELTA_MINUTEN ||
    kmVerschil >= REIS_AFWIJKING_MIN_DELTA_KM
  )
}

// ─── Week-signaal-detectie (Deel 3) ──────────────────────────────────────────

/**
 * Instelbare drempels voor de week-signaal-detectie. Geen hardcoded waarden:
 * de cron leest ze uit `ReistijdSignaalInstelling` met expliciete defaults
 * (zie apps/api/src/lib/instellingen-reistijd-signaal.ts) en geeft ze hier mee.
 */
export interface ReistijdSignaalDrempels {
  /** Minimaal aantal ritten in het venster (default 4). */
  minRitten: number
  /** Minimale absolute mediaan-afwijking in minuten (default 10). */
  minMediaanMinuten: number
  /**
   * Consistentie-drempel als percentage (0..100, default 75): minstens dit
   * aandeel ritten moet dezelfde kant op wijken (én met ≥ de helft van de
   * mediaan-afwijking) wil het signaal vuren.
   */
  consistentiePercentage: number
}

/** Eén rit in het venster — alleen de afwijking (werkelijk − berekend) in minuten. */
export interface ReistijdRit {
  afwijkingMinuten: number
}

export type ReistijdAfwijkingRichting = 'TE_LANG' | 'TE_KORT'

export interface ReistijdAfwijkingResultaat {
  /** True wanneer alle drie de criteria gehaald worden. */
  trigger: boolean
  aantalRitten: number
  /** Mediaan van de afwijkingen (+/−), in minuten. */
  mediaanAfwijkingMinuten: number
  /** Richting van de consistente afwijking, of null bij mediaan 0 / geen trigger. */
  richting: ReistijdAfwijkingRichting | null
  /** Aandeel ritten (0..1) dat dezelfde kant op wijkt met ≥ de helft van de mediaan. */
  consistentieRatio: number
  /** Reden waarom er géén trigger is (voor logging); null bij trigger. */
  reden: string | null
}

/**
 * Pure week-signaal-detectie over de ritten in een rollend venster. Vuurt
 * alleen als ALLE drie de criteria worden gehaald (spec Deel 3a):
 *
 *   1. minimaal `minRitten` ritten in het venster;
 *   2. |mediaan-afwijking| ≥ `minMediaanMinuten` in dezelfde richting
 *      (consistent te lang of consistent te kort);
 *   3. spreiding klein genoeg: ≥ `consistentiePercentage`% van de ritten wijkt
 *      dezelfde kant op als de mediaan, met ≥ de helft van de mediaan-afwijking.
 *
 * Volledig deterministisch en zonder side-effects zodat de detectie los van de
 * DB unit-getest wordt (trigger / geen-trigger / wisselende richtingen).
 */
export function detecteerReistijdAfwijking(
  ritten: ReistijdRit[],
  drempels: ReistijdSignaalDrempels,
): ReistijdAfwijkingResultaat {
  const afwijkingen = ritten.map((r) => r.afwijkingMinuten)
  const aantalRitten = afwijkingen.length
  const med = mediaan(afwijkingen)
  const richting: ReistijdAfwijkingRichting | null =
    med > 0 ? 'TE_LANG' : med < 0 ? 'TE_KORT' : null

  // Consistentie: ritten die dezelfde kant op wijken als de mediaan, met
  // minstens de helft van de mediaan-grootte. Bij mediaan 0 is er geen richting.
  const halveMediaan = Math.abs(med) / 2
  const matching =
    med === 0
      ? 0
      : afwijkingen.filter((a) => a * med > 0 && Math.abs(a) >= halveMediaan).length
  const consistentieRatio = aantalRitten === 0 ? 0 : matching / aantalRitten

  let reden: string | null = null
  if (aantalRitten < drempels.minRitten) {
    reden = `te weinig ritten (${aantalRitten} < ${drempels.minRitten})`
  } else if (richting === null) {
    reden = 'mediaan-afwijking is 0 (geen richting)'
  } else if (Math.abs(med) < drempels.minMediaanMinuten) {
    reden = `mediaan-afwijking te klein (${Math.abs(med).toFixed(1)} < ${drempels.minMediaanMinuten} min)`
  } else if (consistentieRatio < drempels.consistentiePercentage / 100) {
    reden = `niet consistent genoeg (${Math.round(consistentieRatio * 100)}% < ${drempels.consistentiePercentage}%)`
  }

  return {
    trigger: reden === null,
    aantalRitten,
    mediaanAfwijkingMinuten: med,
    richting,
    consistentieRatio,
    reden,
  }
}
